/**
 * AP-35 Detailblatt 7 — **Verkauf und Abrechnung** (Baustein `PROJECT_MGMT.BILLING`).
 * Der Vertrag zwischen Backend und ERP.
 */

/** Blatt 7 2.2 — abgeleitet, nie gespeichert. */
export const SALES_LINE_STATES = ['OPEN', 'DELIVERED', 'INVOICED', 'CREDITED', 'NOT_BILLABLE'] as const
export type SalesLineState = (typeof SALES_LINE_STATES)[number]

export const SALES_LINE_STATE_LABELS: Readonly<Record<SalesLineState, string>> = {
  OPEN: 'offen',
  DELIVERED: 'geliefert',
  INVOICED: 'abgerechnet',
  CREDITED: 'gutgeschrieben',
  NOT_BILLABLE: 'nicht abrechenbar',
}

/**
 * Blatt 7 2.1, Nachtrag 12 — die Arten der Verkaufszeile.
 *
 * `TITLE` kam mit Nachtrag 12 dazu, weil die Zeile später in den Beleg wandert
 * und eine Überschrift dort mitgeht. **Zwischensumme, Abzug und Seitenumbruch
 * gibt es bewusst nicht**: Sie sind Rechen- und Druckwerk des Belegs, während
 * eine Verkaufszeile ein abrechenbarer Anspruch ist (Entscheidung N-108).
 */
export const SALES_LINE_TYPES = ['ITEM', 'TEXT', 'TITLE'] as const
export type SalesLineType = (typeof SALES_LINE_TYPES)[number]

export const SALES_LINE_TYPE_LABELS: Readonly<Record<SalesLineType, string>> = {
  ITEM: 'Zeile',
  TEXT: 'Text',
  TITLE: 'Überschrift',
}

/** Was der Gesamtpreis einer Verkaufszeile braucht. */
export interface VerkaufZeilenPreis {
  quantity: number
  unitPrice: number | null
  priceUnit: number
  discountPercent: number | null
}

/**
 * GP = Menge × EP ÷ PEH × (1 − Rabatt), auf Cent.
 *
 * **Eine Formel für Server und Browser:** Der Server rechnet damit die gespeicherte Zeile, die Maske damit
 * die Zeile, während sie getippt wird — sonst spränge der Betrag beim Speichern.
 */
export function verkaufZeilenSumme(z: VerkaufZeilenPreis): number {
  const peh = Math.max(1, z.priceUnit || 1)
  return Math.round(((z.quantity * (z.unitPrice ?? 0)) / peh) * (1 - (z.discountPercent ?? 0) / 100) * 100) / 100
}

export const SALES_LINE_ORIGINS = ['MANUAL', 'DOCUMENT', 'LV', 'REGIE', 'CONSUMABLE', 'CALCULATION', 'SPLIT', 'COPY'] as const
export type SalesLineOrigin = (typeof SALES_LINE_ORIGINS)[number]

export const SALES_LINE_ORIGIN_LABELS: Readonly<Record<SalesLineOrigin, string>> = {
  MANUAL: 'von Hand',
  DOCUMENT: 'aus Beleg',
  LV: 'aus LV',
  REGIE: 'Regie',
  CONSUMABLE: 'Verbrauch',
  CALCULATION: 'aus Kalkulation',
  SPLIT: 'geteilt',
  COPY: 'kopiert',
}

/** Blatt 7 3.2 — Gruppierung des Regievorschlags (Prisma `ProjectRegieGrouping`). */
export const REGIE_GROUPINGS = ['QUALIFICATION_DAY', 'QUALIFICATION', 'USER_DAY', 'DAY'] as const
export type RegieGrouping = (typeof REGIE_GROUPINGS)[number]

export const REGIE_GROUPING_LABELS: Readonly<Record<RegieGrouping, string>> = {
  QUALIFICATION_DAY: 'je Qualifikation und Tag',
  QUALIFICATION: 'je Qualifikation gesamt',
  USER_DAY: 'je Mitarbeiter und Tag',
  DAY: 'je Tag',
}

/** Blatt 7 5.2 — die Reiter des Abrechnungsarbeitsplatzes. */
export const BILLING_WORKPLACE_TABS = ['due', 'regie', 'deliverable', 'billable', 'done'] as const
export type BillingWorkplaceTab = (typeof BILLING_WORKPLACE_TABS)[number]

/** Belegarten, die eine Verkaufszeile abrechnen (6.). */
export const SALES_INVOICE_KINDS = ['INVOICE', 'FINAL_INVOICE'] as const
/** Teil- und Anzahlungsrechnungen — Prozentsatz der Auftragsbestätigung (5.1). */
export const INSTALLMENT_INVOICE_KINDS = ['DOWN_PAYMENT_INVOICE', 'PARTIAL_INVOICE'] as const

export interface SalesLineView {
  id: string
  /** AP-36 8.1: Stand der Zeile — die Maske schreibt dagegen. */
  updatedAt?: string
  projectId: string
  positionId: string
  parentId: string | null
  sortOrder: number
  lineType: SalesLineType
  subMode: 'SUM_OF_CHILDREN' | 'OWN_PRICE' | null
  quantity: number
  unitId: string | null
  unitCode: string | null
  /** Nachtrag 12: Menge in einer Alternativeinheit — Angabe, kein Rechenweg (wie am Beleg). */
  altQuantity: number | null
  altUnitCode: string | null
  name: string
  longText: string | null
  /** Nachtrag 12: einleitender Text **vor** der Position; `longText` steht darunter. */
  introText: string | null
  /** Nachtrag 12: Auftragsnummer nur dieser Zeile — sie wandert in den Beleg. */
  customerReference: string | null
  articleId: string | null
  articleNumber: string | null
  /** AP-35 J: aus dem LV dieses Projekts. */
  lvItemId?: string | null
  /**
   * AP-35 J (Blatt 12 6.): OZ und — wenn eine neue LV-Version die Position geändert
   * hat — je Feld alt → neu; `removed` heißt „im LV entfallen". Nichts wird von
   * selbst übernommen.
   */
  lv?: { oz: string; removed: boolean; successorId: string | null; changes: { field: string; before: string | number | null; after: string | number | null }[] } | null
  /** Nur mit `projects.price.view`. */
  unitPrice?: number | null
  priceUnit: number
  discountPercent?: number | null
  lineTotal?: number | null
  taxRate: number | null
  origin: SalesLineOrigin
  isBillable: boolean
  /**
   * Nachtrag 12: ab wann die Zeile verrechnet werden darf (`JJJJ-MM-TT`).
   * **Leer heißt sofort.** Geprüft wird beim Erzeugen der Rechnung, nicht nur
   * angezeigt; der Lieferschein bleibt unberührt.
   */
  billableFrom: string | null
  internalNote: string | null
  state: SalesLineState
  deliveredAt: string | null
  deliveryNote: { id: string; number: string | null } | null
  invoice: { id: string; number: string | null } | null
  creditNote: { id: string; number: string | null } | null
  /** Entwürfe, in denen die Zeile gerade steht — eine Marke, keine Wahrheit. */
  inDrafts: string[]
  /** Regie: Anzahl der abgerechneten Zeitbuchungen. */
  timeEntries: number
  /** Nachtrag 12: wer die Zeile angelegt hat und wann. */
  createdAt: string
  createdBy: SalesLineUser | null
  /** Nachtrag 12: wer zuletzt geändert hat. Leer, solange nur der Anleger geschrieben hat. */
  updatedBy: SalesLineUser | null
  children: SalesLineView[]
}

/** Nachtrag 12 — Benutzer an einer Verkaufszeile: Kennung und angezeigter Name. */
export interface SalesLineUser {
  id: string
  name: string
}

/** Blatt 7 3.3 — eine Zeitbuchung im Regievorschlag. */
export interface RegieEntryView {
  timeEntryId: string
  date: string
  userId: string
  userName: string
  qualification: string | null
  qualificationId: string | null
  hours: number
  /** Nur mit Preisrecht. */
  rate?: number | null
  /** `null`, wenn frei; sonst warum nicht wählbar. */
  blockedBy: null | { code: 'REGIE_ENTRY_ALREADY_BILLED' | 'REGIE_ENTRY_IN_SITE_REPORT'; label: string }
  hints: string[]
}

export interface RegieProposalGroup {
  key: string
  name: string
  qualificationId: string | null
  hours: number
  rate?: number | null
  total?: number | null
  longText: string
  internalNote: string
  entryIds: string[]
  hints: string[]
}

export interface RegieProposal {
  positionId: string
  until: string
  grouping: RegieGrouping
  namesOnInvoice: boolean
  scheme: string | null
  /** Hinweis, wenn die Position nicht nach Regie abgerechnet wird (9. `BILLING_NOT_TIME_AND_MATERIAL`). */
  schemeWarning: boolean
  entries: RegieEntryView[]
  groups: RegieProposalGroup[]
  /** AP-27 4.2: Regieberichte dieses Projekts, die laut Herzschlag noch auf einem Gerät liegen. */
  reportsOnDevice?: { number: string; userName: string; documentDate: string }[]
  /** Phase I: Verbrauchsmaterial ohne Verkaufszeile (Blatt 7 3.2). */
  consumables: {
    consumableId: string
    date: string
    name: string
    quantity: number
    unitCode: string | null
    needsArticle: boolean
    unitPrice?: number | null
    total?: number | null
  }[]
  hours: number
  total?: number | null
}
